import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Search, Building2, Calendar, FlaskConical, Car, Users, ClipboardList, Heart, Plus, HeartHandshake, Sparkles, CheckCircle2, ArrowRight } from "lucide-react";

const QUICK_CARDS = [
  { to: "/filho-presente/profissionais", icon: Search, title: "Encontrar profissional", desc: "Geriatra, fisio, psicóloga..." },
  { to: "/filho-presente/clinicas", icon: Building2, title: "Encontrar clínica", desc: "Consultórios, laboratórios, imagem" },
  { to: "/filho-presente/solicitar-consulta", icon: Calendar, title: "Agendar consulta", desc: "Presencial, online ou domiciliar" },
  { to: "/filho-presente/solicitar-exame", icon: FlaskConical, title: "Solicitar exame", desc: "Anexe o pedido médico" },
  { to: "/filho-presente/solicitar-transporte", icon: Car, title: "Solicitar transporte", desc: "Ida e volta da consulta" },
  { to: "/filho-presente/familiares", icon: Users, title: "Meus familiares", desc: "Cadastros de beneficiários" },
  { to: "/filho-presente/solicitacoes", icon: ClipboardList, title: "Meus atendimentos", desc: "Acompanhe status e histórico" },
  { to: "/filho-presente/plano-cuidados", icon: Heart, title: "Plano de cuidados", desc: "Rotina de acompanhamento" },
];

export default function FilhoPresenteDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [openRequests, setOpenRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [b, r] = await Promise.all([
          api.get("/fp/beneficiaries"),
          api.get("/fp/requests", { params: { status: "solicitada" } }).catch(() => ({ data: [] })),
        ]);
        setBeneficiaries(b.data || []);
        setOpenRequests(r.data || []);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="fp-dashboard">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">
            <HeartHandshake className="w-3.5 h-3.5 text-amber-500" /> Filho Presente
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#0B2B62] tracking-tight">
            Olá, {(user?.name || "").split(" ")[0]} 👋
          </h1>
          <p className="text-slate-600 mt-1 text-sm sm:text-base">Cuidado organizado. Família tranquila.</p>
        </div>
        <Link to="/filho-presente/familiares/novo" data-testid="fp-new-beneficiary"
          className="inline-flex items-center gap-2 bg-[#0B2B62] text-white px-4 py-2.5 rounded-lg hover:bg-slate-800 transition-colors text-sm font-medium">
          <Plus className="w-4 h-4" /> Cadastrar familiar
        </Link>
      </div>

      {/* Banner: escolha de plano — visível apenas se ainda não é assinante ativo */}
      <PlanBanner />

      {/* Meus familiares */}
      <section className="mb-10">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-3">Meus familiares</div>
        {loading ? (
          <div className="text-slate-500 text-sm">Carregando...</div>
        ) : beneficiaries.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-slate-300 rounded-xl p-8 text-center">
            <Users className="w-8 h-8 text-slate-400 mx-auto mb-3" />
            <div className="text-slate-700 font-medium mb-1">Nenhum familiar cadastrado ainda</div>
            <div className="text-slate-500 text-sm mb-4">Comece cadastrando o beneficiário para organizar os cuidados.</div>
            <Link to="/filho-presente/familiares/novo" className="inline-flex items-center gap-2 bg-[#0B2B62] text-white px-4 py-2 rounded-lg text-sm">
              <Plus className="w-4 h-4" /> Cadastrar agora
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {beneficiaries.map((b) => (
              <button key={b.id} onClick={() => nav(`/filho-presente/familiares/${b.id}`)}
                data-testid={`fp-beneficiary-${b.id}`}
                className="text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-[#0B2B62] hover:shadow-md transition-all">
                <div className="flex items-center gap-3">
                  {b.photo_url ? (
                    <img src={b.photo_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold">
                      {(b.name || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-800 truncate">{b.name}</div>
                    <div className="text-xs text-slate-500 truncate">{b.city ? `${b.city}${b.state ? "/" + b.state : ""}` : b.relation_to_owner || "—"}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Cards de ação */}
      <section className="mb-10">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-3">Acesso rápido</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {QUICK_CARDS.map((c) => (
            <Link key={c.to} to={c.to} data-testid={`fp-action-${c.to.split("/").pop()}`}
              className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 hover:border-[#0B2B62] hover:shadow-md transition-all group">
              <c.icon className="w-6 h-6 text-[#0B2B62] mb-3 group-hover:scale-110 transition-transform" strokeWidth={1.8} />
              <div className="font-semibold text-slate-800 text-sm sm:text-base leading-tight">{c.title}</div>
              <div className="text-xs text-slate-500 mt-1 hidden sm:block">{c.desc}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Solicitações abertas */}
      {openRequests.length > 0 && (
        <section>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-3">Solicitações em andamento</div>
          <div className="bg-white border border-slate-200 rounded-xl divide-y">
            {openRequests.slice(0, 6).map((r) => (
              <Link key={r.id} to={`/filho-presente/solicitacoes/${r.id}`}
                className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">
                    {r.type === "consulta" && `Consulta · ${r.specialty || "—"}`}
                    {r.type === "exame" && `Exame · ${r.exam_type}`}
                    {r.type === "transporte" && `Transporte · ${r.origin}`}
                    {r.type === "acompanhante" && "Acompanhante"}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {r.beneficiary_name} · {r.desired_date || "sem data"}
                  </div>
                </div>
                <span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700 shrink-0">
                  {r.status?.replace("_", " ")}
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// Banner que sugere assinar um plano. Some quando o usuário já tem plano
// ativo. Aparece no topo do painel FP pra dar visibilidade ao caminho de
// compra, já que o usuário chegava aqui sem saber onde assinar.
const PlanBanner = () => {
  const [state, setState] = useState({ loading: true, plan: null, status: null });
  useEffect(() => {
    api.get("/fp/plans/me").then((r) => setState({
      loading: false, plan: r.data?.plan, status: r.data?.status,
    })).catch(() => setState({ loading: false, plan: null, status: null }));
  }, []);
  if (state.loading || (state.plan && state.status === "active")) return null;
  return (
    <div data-testid="fp-plan-banner"
      className="mb-8 rounded-2xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 via-white to-amber-50 p-5 sm:p-6 flex items-center gap-4 flex-wrap">
      <div className="w-11 h-11 rounded-xl bg-amber-400 flex items-center justify-center shrink-0">
        <Sparkles className="w-5 h-5 text-[#0B2B62]" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-[240px]">
        <div className="text-[10px] uppercase tracking-widest text-amber-800 font-bold">Assinatura Filho Presente</div>
        <div className="font-display font-bold text-[#0B2B62] text-lg mt-0.5 leading-tight">
          Escolha um plano e destrave o cuidado completo
        </div>
        <div className="text-xs text-slate-600 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-600" /> Assistente Sofia</span>
          <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-600" /> Prioridade no consultor</span>
          <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-600" /> Lembretes WhatsApp</span>
        </div>
      </div>
      <Link to="/filho-presente/planos" data-testid="fp-plan-cta"
        className="inline-flex items-center gap-2 bg-amber-400 text-[#0B2B62] font-bold px-5 py-2.5 rounded-lg hover:bg-amber-300 transition-colors text-sm shadow">
        Ver planos <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
};
